import { MAX_CACHED_POINTS_PER_DEVICE } from 'features/Integrations/utils/integrationDataUtils';
import { normalizeDevice } from 'features/Integrations/utils/integrationNormalize';
import apiClient from 'services/_infra/apiClient';
import { loginfo } from 'services/_infra/util/apiUsageLogger';
import { devicesApi } from './devices.api';

// An event data point without a usable ts (the dummy TB runner's idle flip, for one)
// still lands at data[0] — so the on-screen status flips — but sorts to the bottom of
// every ts-ordered history and is dropped by the next mergeDeviceDataByTimestamp, which
// keys on ts. Stamp arrival time so the event stays visible and ordered.
const withArrivalTs = (point) => {
  const ts = Number(point?.ts);
  return Number.isFinite(ts) && ts > 0 ? point : { ...point, ts: Date.now() };
};

// Allows the updating of one or more devices in the RTK Query cache based on an event received from the server
export const applyDeviceUpdatesFromEvent = (dispatch, updates) => {
  let updatesArray;

  if (Array.isArray(updates)) {
    updatesArray = updates;
  } else if (updates) {
    updatesArray = [updates];
  } else {
    updatesArray = [];
  }

  if (!updatesArray.length) return;

  dispatch(
    devicesApi.util.updateQueryData(
      devicesApi.endpoints.getConnectedDevices.name,
      undefined,
      (draft) => {
        if (!draft.length) return;

        updatesArray.forEach((update) => {
          const indexOfDeviceToUpdate = draft.findIndex((device) => device.id === update.id);

          if (indexOfDeviceToUpdate < 0) return; // If the device is not found in the cache, skip it

          const normalizedUpdate = normalizeDevice(update);

          const prevDevice = draft[indexOfDeviceToUpdate];
          draft[indexOfDeviceToUpdate] = {
            ...prevDevice,
            ...normalizedUpdate,
            coordinates: normalizedUpdate?.coordinates || prevDevice.coordinates,
            location: normalizedUpdate?.location || prevDevice.location,
            // Newest first; the socket path is capped so the cache cannot grow without bound.
            data: [
              ...(Array.isArray(normalizedUpdate?.data) ? normalizedUpdate.data : []).map(
                withArrivalTs,
              ),
              ...(prevDevice?.data || []),
            ].slice(0, MAX_CACHED_POINTS_PER_DEVICE),
          };
        });
      },
    ),
  );
};

// Replaces the entire cached device types array with a new one based on event received from the server
export const updateDeviceTypesFromEvent = (dispatch, deviceTypes) => {
  if (!deviceTypes) return;

  dispatch(
    devicesApi.util.updateQueryData(
      devicesApi.endpoints.getAllAvailableDeviceTypes.name,
      undefined,
      () => deviceTypes,
    ),
  );
};

export const addDeviceFromEvent = (dispatch, eventData) => {
  const { device, deviceTypes } = eventData || {};

  if (!device) {
    console.warn(
      '[device-add] No "device" key in payload. Expected { device, deviceTypes }, got:',
      eventData,
    );
    return;
  }

  const normalizedDevice = normalizeDevice(device);
  if (!normalizedDevice?.id) return;

  dispatch(
    devicesApi.util.updateQueryData(
      devicesApi.endpoints.getConnectedDevices.name,
      undefined,
      (draft) => {
        const existingDeviceIndex = draft.findIndex((device) => device.id === normalizedDevice.id); // Check if the device already exists in the cache

        if (existingDeviceIndex >= 0) {
          draft[existingDeviceIndex] = {
            ...draft[existingDeviceIndex],
            ...normalizedDevice,
          };
        } else {
          draft.push(normalizedDevice);
        }

        if (deviceTypes) {
          updateDeviceTypesFromEvent(dispatch, deviceTypes);
        }
      },
    ),
  );
};

export const removeDeviceFromEvent = (dispatch, eventData) => {
  const { id, deviceTypes } = eventData || {};
  if (!id) {
    console.warn('[device-remove] No "id" in payload. Got:', eventData);
    return;
  }

  dispatch(
    devicesApi.util.updateQueryData(
      devicesApi.endpoints.getConnectedDevices.name,
      undefined,
      (draft) => draft.filter((device) => device.id !== id),
    ),
  );

  if (deviceTypes) {
    updateDeviceTypesFromEvent(dispatch, deviceTypes);
  }
};

// One PAGE of data points for NAMED devices (max 25 ids) in a time range, newest first.
// Not cached with RTK Query: the export modal pages through it with changing offsets.
// The response body carries `result` (array of per-device arrays) and
// `page = { limit, offset, hasMore, returned }` — loop on `hasMore`.
export async function getNodeDataByTimestamps(startTime, endTime, ids, { limit, offset } = {}) {
  try {
    return await apiClient.get('/v2/integrations/device/data', {
      params: {
        startTime,
        endTime,
        ids: ids ? (Array.isArray(ids) ? ids.join(',') : ids) : undefined,
        limit,
        offset,
      },
    });
  } catch (error) {
    console.error('/v2/integrations/device/data failed.', error);
  }
}

export async function getSystemTypeMap() {
  try {
    return await apiClient.get('/v2/integrations/system/map');
  } catch (error) {
    console.error('/v2/integrations/system/map failed.', error);
  }
}

export async function getAudioFile(audioId) {
  try {
    loginfo();
    return await apiClient.get(`/v2/integrations/device/audio/${audioId}`, {
      responseType: 'blob',
    });
  } catch (error) {
    console.error('getAudioFile logging failed.', error);
  }
}
